import React, { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronDown, CreditCard, Megaphone, Target, Sparkles, Rocket, BarChart3, Shield, BookOpen } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { ModalsContext } from 'components/context/ModalsContext';
import { useTour } from 'components/context/TourContext';

const SECTIONS = [
  {
    id: 'tutorial',
    icon: BookOpen,
    title: 'Recorrido guiado',
    description: 'Un tour rápido por la app para conocer las secciones principales: Dashboard, Objetivos, Optimizaciones, Campañas y más.',
    action: { type: 'startTour' },
  },
  {
    id: 'empezar',
    icon: Rocket,
    title: 'Para comenzar',
    description: 'espor.ai es una plataforma de gestión y optimización de campañas digitales basada en datos e inteligencia artificial.',
    blocks: [
      { type: 'paragraph', text: 'espor.ai es una plataforma de gestión y optimización de campañas digitales basada en datos e inteligencia artificial.' },
      { type: 'heading', text: '👉 Requisito indispensable' },
      { type: 'paragraph', text: 'Para poder usar espor.ai necesitás tener al menos una cuenta de Google Ads o Meta Ads (Facebook / Instagram). Sin una cuenta conectada, la plataforma no puede analizar datos ni generar objetivos, optimizaciones o ayudarte a crear campañas.' },
      { type: 'heading', text: '¿No tenés cuentas publicitarias todavía?' },
      { type: 'paragraph', text: 'No hay problema.' },
      { type: 'paragraph', text: 'Desde la sección "Marcas y conexiones" podés conectar Google Ads y Meta Ads, elegir cuentas activas y completar permisos.' },
      { type: 'list', items: ['Crear o usar una cuenta de Google Ads y/o Meta Ads', 'Verificar accesos y permisos', 'Vincular páginas, catálogos o WhatsApp Business cuando corresponda'] },
      { type: 'paragraph', text: 'Una vez conectado, la plataforma puede analizar datos y ayudarte a crear campañas u optimizaciones.' },
    ],
    action: { label: 'Ir a Marcas y conexiones', path: '/profile/brands' },
  },
  {
    id: 'objetivos',
    icon: Target,
    title: 'Objetivos',
    description: 'Los objetivos son el corazón de espor.ai.',
    blocks: [
      { type: 'paragraph', text: 'Los objetivos son el corazón de espor.ai.' },
      { type: 'paragraph', text: 'La plataforma utiliza inteligencia artificial para:' },
      { type: 'list', items: ['Analizar tu historial de datos', 'Entender cómo viene rindiendo tu cuenta', 'Proponer objetivos realistas y accionables'] },
      { type: 'heading', text: '¿Qué tipo de objetivos podés usar?' },
      { type: 'paragraph', text: 'Podés definir objetivos basados en:' },
      { type: 'list', items: ['ROAS', 'CPA', 'Inversión', 'Conversiones'] },
      { type: 'heading', text: '¿Cómo funciona la IA?' },
      { type: 'paragraph', text: 'La IA analiza tu pasado para predecir el futuro. No inventa números: se basa en el comportamiento real de tus campañas.' },
      { type: 'heading', text: 'Nivel de personalización' },
      { type: 'paragraph', text: 'Los objetivos son tan simples o tan específicos como quieras:' },
      { type: 'list', items: ['Podés incluir todas las campañas o solo algunas', 'Podés usar todas las conversiones o seleccionar conversiones específicas', 'Podés crear objetivos generales o muy finos (por ejemplo: ROAS puntual para una campaña específica)'] },
      { type: 'paragraph', text: 'Siempre mantenés el control: 👉 podés usar los objetivos sugeridos por la IA o ajustarlos manualmente.' },
    ],
    action: { label: 'Ir a Objetivos', path: '/objetivos' },
  },
  {
    id: 'optimizaciones',
    icon: Sparkles,
    title: 'Optimizaciones',
    description: 'La sección de optimizaciones te dice qué hacer y por qué.',
    blocks: [
      { type: 'paragraph', text: 'La sección de optimizaciones te dice qué hacer y por qué, sin que tengas que revisar campañas, métricas y reportes uno por uno.' },
      { type: 'paragraph', text: 'espor.ai analiza tus cuentas todos los días y te muestra oportunidades concretas para mejorar resultados y alcanzar (o superar) tus objetivos.' },
      { type: 'heading', text: '¿Cómo detecta oportunidades espor.ai?' },
      { type: 'paragraph', text: 'Nuestro sistema:' },
      { type: 'list', items: ['Analiza los últimos 21 días de datos', 'Compara el rendimiento real contra los objetivos que definiste', 'Identifica desvíos, oportunidades y puntos de mejora en base a datos reales'] },
      { type: 'paragraph', text: 'El foco no está en mostrarte números, sino en ayudarte a tomar decisiones.' },
      { type: 'heading', text: '¿Qué tipo de optimizaciones vas a ver?' },
      { type: 'paragraph', text: 'Algunos ejemplos:' },
      { type: 'list', items: ['Campañas que están rindiendo por debajo del objetivo', 'Oportunidades para escalar campañas que vienen funcionando bien', 'Elementos que están consumiendo presupuesto sin generar resultados', 'Ajustes sugeridos para acercarte o superar tus objetivos'] },
      { type: 'paragraph', text: 'No son alertas genéricas. 👉 Cada optimización está contextualizada según tu cuenta, tus campañas y tus objetivos.' },
      { type: 'heading', text: 'Tu feedback es clave' },
      { type: 'paragraph', text: 'Es muy importante que indiques si una sugerencia te resulta útil o no.' },
      { type: 'heading', text: '¿Por qué?' },
      { type: 'list', items: ['Nuestro motor interno aprende en base a tu feedback', 'Entiende qué tipo de optimizaciones te sirven más', 'Ajusta y mejora las sugerencias futuras'] },
      { type: 'paragraph', text: 'Cuanto más interactuás con las optimizaciones, 👉 mejor y más preciso se vuelve espor.ai para tu cuenta.' },
      { type: 'paragraph', text: 'Pensalo como entrenar a tu propio copiloto de optimización.' },
    ],
    action: { label: 'Ir al Optimizador', path: '/optimizador' },
  },
  {
    id: 'campañas',
    icon: Megaphone,
    title: 'Campañas',
    description: 'Creá y modificá campañas en Google, Meta y WhatsApp con asistencia de IA.',
    blocks: [
      { type: 'paragraph', text: 'Desde espor.ai podés crear campañas nuevas y editar las que ya tenés en Google Ads, Meta Ads o WhatsApp. Todo en un mismo lugar, con un asistente con IA que te guía en cada paso.' },
      { type: 'heading', text: 'Crear campañas nuevas' },
      { type: 'paragraph', text: 'El flujo de creación te permite:' },
      { type: 'list', items: ['Elegir la plataforma (Google Ads, Meta Ads o WhatsApp) y el tipo de campaña', 'Definir presupuesto, audiencias, fechas y objetivos', 'Generar headlines, descripciones y creativos con ayuda de la IA', 'Revisar el preview antes de publicar'] },
      { type: 'paragraph', text: 'El asistente con IA entiende qué querés lograr y te propone textos y configuraciones en base a mejores prácticas y tu contexto.' },
      { type: 'heading', text: 'Modificar campañas existentes' },
      { type: 'paragraph', text: 'Las campañas que ya tenés conectadas también se pueden editar desde espor.ai:' },
      { type: 'list', items: ['Ver el detalle de cada campaña (creativos, configuración, métricas)', 'Ajustar presupuestos, fechas y objetivos', 'Cambiar textos y creativos con sugerencias de la IA', 'Mantener el control sobre qué cambios se aplican realmente'] },
      { type: 'paragraph', text: '👉 Podés crear campañas desde cero o tomar una existente y mejorarla; la IA te ayuda en ambos casos.' },
      { type: 'heading', text: 'Dónde ver todas tus campañas' },
      { type: 'paragraph', text: 'En la sección Campañas vas a encontrar el listado de tus campañas por plataforma. Desde ahí accedés al detalle de cada una, a la creación de nuevas y a los reportes de rendimiento.' },
    ],
    action: { label: 'Ir a Campañas', path: '/campaigns' },
  },
  {
    id: 'analytics',
    icon: BarChart3,
    title: 'Analytics',
    description: 'Métricas e insights de tus campañas en un solo lugar.',
    blocks: [
      { type: 'paragraph', text: 'En Analytics ves el rendimiento de tus campañas de Google Ads y Meta Ads en un solo lugar. No hace falta saltar entre plataformas: inversión, impresiones, clics, conversiones y costos principales están reunidos en dashboards y tablas.' },
      { type: 'heading', text: '¿Qué podés ver?' },
      { type: 'paragraph', text: 'Podés revisar:' },
      { type: 'list', items: ['Rendimiento por campaña, por plataforma o por período (día, semana, mes)', 'Inversión total y por plataforma, con comparativas y evolución en el tiempo', 'Métricas clave: impresiones, clics, conversiones, CPA, ROAS y costos asociados', 'Distribución del presupuesto y del rendimiento entre campañas y canales'] },
      { type: 'heading', text: 'Filtros y profundización' },
      { type: 'paragraph', text: 'Usá filtros por fecha, plataforma y campaña para enfocarte en lo que te importa. Así podés analizar un período puntual, comparar plataformas o bajar al detalle de una campaña específica sin perder el contexto general.' },
      { type: 'heading', text: 'Insights con IA' },
      { type: 'paragraph', text: 'Los insights con IA resumen tendencias, detectan patrones y señalan oportunidades de mejora en base a tus datos reales. No reemplazan tu criterio: te ayudan a ver rápido qué está funcionando y qué conviene ajustar.' },
      { type: 'paragraph', text: '👉 Pensá en Analytics como tu panel de control: números claros, en un solo lugar, listos para tomar decisiones.' },
    ],
    action: { label: 'Ver Analytics', path: '/analytics20' },
  },
  {
    id: 'datos',
    icon: Shield,
    title: 'Manejo de datos',
    description: 'Cómo se guardan, usan y protegen tus datos en espor.ai.',
    blocks: [
      { type: 'paragraph', text: 'En espor.ai usamos tus datos de campañas y cuentas conectadas solo para darte insights, optimizaciones, objetivos y métricas dentro de la plataforma. No vendemos ni compartimos tu información con terceros con fines de publicidad.' },
      { type: 'heading', text: '¿Qué datos usamos?' },
      { type: 'paragraph', text: 'Trabajamos con la información que vos elegís conectar:' },
      { type: 'list', items: ['Datos de tus cuentas de Google Ads y/o Meta Ads (campañas, métricas, conversiones)', 'Información de tu perfil y de tu negocio que cargás en la plataforma', 'Tu interacción con objetivos, optimizaciones y sugerencias para mejorar las recomendaciones'] },
      { type: 'heading', text: 'Conexiones con Google y Meta' },
      { type: 'paragraph', text: 'Las conexiones con Google Ads y Meta Ads se hacen con permisos limitados y siguiendo las políticas y estándares de seguridad de cada plataforma. Solo accedemos a lo necesario para mostrarte métricas, proponer objetivos y darte optimizaciones.' },
      { type: 'heading', text: 'Tu control sobre los datos' },
      { type: 'paragraph', text: 'Siempre mantenés el control:' },
      { type: 'list', items: ['Podés desconectar cuentas de Google o Meta cuando quieras desde Perfil → Marcas', 'Podés actualizar o eliminar datos de tu perfil desde la sección de ajustes de cuenta', 'Podés revisar qué cuentas y marcas están vinculadas a tu usuario'] },
      { type: 'paragraph', text: '👉 Para más información sobre privacidad, tratamiento de datos y obligaciones legales, consultá siempre los términos de uso y la política de privacidad de espor.ai.' },
    ],
    action: { label: 'Ir a Perfil', path: '/profile' },
  },
  {
    id: 'precios',
    icon: CreditCard,
    title: 'Precios y planes',
    description: 'Planes y funcionalidades incluidas en cada uno.',
    blocks: [
      { type: 'paragraph', text: 'espor.ai ofrece planes gratuitos y de pago según el uso y las funcionalidades que necesites. En cada plan se detallan los límites y lo que está incluido.' },
      { type: 'heading', text: '¿Qué incluye cada plan?' },
      { type: 'paragraph', text: 'Según el plan que elijas, podés tener acceso a:' },
      { type: 'list', items: ['Cantidad de campañas que podés crear y gestionar', 'Objetivos y optimizaciones disponibles', 'Acceso a Analytics e insights con IA', 'Soporte y características avanzadas'] },
      { type: 'paragraph', text: 'Los planes se adaptan a cuentas que recién arrancan y a equipos que ya escalaron. 👉 Revisá los precios actuales y los límites de cada plan en el detalle de planes.' },
      { type: 'heading', text: 'Cómo cambiar de plan' },
      { type: 'paragraph', text: 'Podés cambiar de plan en cualquier momento desde Perfil → Suscripción. Ahí ves tu plan actual, las opciones disponibles y cómo upgradar o ajustar según lo que necesites.' },
      { type: 'paragraph', text: 'Para ver precios, condiciones y comparar planes, abrí el modal de "Ver planes y precios" desde esta sección o desde los avisos de la plataforma.' },
    ],
    action: { type: 'plans' },
  },
];

const HelpCenterModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { openPlansModal } = useContext(ModalsContext);
  const { startTour } = useTour();
  const [expandedIds, setExpandedIds] = useState(new Set());
  const scrollRef = useRef(null);

  // Scroll arriba al abrir para que los ítems empiecen desde arriba
  useEffect(() => {
    if (isOpen && scrollRef.current) {
      const el = scrollRef.current;
      requestAnimationFrame(() => {
        el.scrollTop = 0;
      });
    }
  }, [isOpen]);

  const toggleSection = (id) => {
    setExpandedIds((prev) => {
      if (prev.has(id)) return new Set(); // cerrar el que está abierto
      return new Set([id]); // abrir solo este y cerrar los demás (acordeón)
    });
  };

  const handleAction = (section, e) => {
    e?.stopPropagation?.();
    if (section.action.type === 'plans') {
      onClose();
      openPlansModal();
    } else if (section.action.type === 'startTour') {
      onClose();
      startTour();
    } else if (section.action.path) {
      onClose();
      navigate(section.action.path);
    }
  };

  const getActionLabel = (section) => {
    if (section.action.type === 'plans') return 'Ver planes y precios';
    if (section.action.type === 'startTour') return 'Iniciar recorrido';
    return section.action.label;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 modal-safe-area z-[10000] flex flex-row"
          role="dialog"
          aria-labelledby="help-center-title"
          aria-modal="true"
        >
          {/* Overlay izquierdo: cubre toda la app (incl. selector de marcas), blur como el resto */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex-1 min-w-0 bg-black/30 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Panel tipo chat a la derecha */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="w-full max-w-[380px] sm:max-w-[400px] flex flex-col h-full bg-gradient-to-b from-slate-800 via-slate-900 to-slate-900 border-l border-white/20 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header fijo: cerrar (izq), Centro de ayuda + WhatsApp (derecha del título) */}
            <div className="shrink-0 flex items-center justify-between gap-3 px-4 sm:px-5 py-4 border-b border-white/10 bg-slate-800/80">
              <button
                onClick={onClose}
                className="p-2 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors shrink-0"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex flex-col items-center justify-center gap-1 text-center min-w-0 flex-1">
                <h2 id="help-center-title" className="text-base sm:text-lg font-bold text-white">
                  Centro de ayuda
                </h2>
                <p className="text-xs text-white/70">Consultas y acceso rápido</p>
              </div>
              <button
                type="button"
                onClick={() => window.open('https://wa.me/59896964204', '_blank', 'noopener,noreferrer')}
                className="p-2 rounded-full bg-[#25D366] hover:bg-[#20BA5A] text-white hover:scale-110 active:scale-95 transition-all duration-300 flex items-center justify-center shrink-0"
                aria-label="Contactar por WhatsApp"
                title="Contactar por WhatsApp"
              >
                <FaWhatsapp className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            {/* Contenido scrolleable: ítems desde arriba, al desplegar crece hacia abajo */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 flex flex-col gap-2 min-h-0">
              {SECTIONS.map((section) => {
                const Icon = section.icon;
                const isExpanded = expandedIds.has(section.id);
                return (
                  <div
                    key={section.id}
                    className="shrink-0 min-h-[52px] rounded-xl border border-white/10 bg-white/5 hover:border-white/20 transition-colors overflow-hidden"
                  >
                    {/* Cabecera siempre visible: click expande/colapsa */}
                    <button
                      type="button"
                      onClick={() => toggleSection(section.id)}
                      className="w-full min-h-[52px] flex items-center gap-3 p-3 sm:p-3.5 text-left hover:bg-white/5 transition-colors"
                      aria-expanded={isExpanded}
                      aria-controls={`help-section-${section.id}`}
                      id={`help-heading-${section.id}`}
                    >
                      <div className="p-1.5 rounded-lg bg-white/10 shrink-0">
                        <Icon className="w-4 h-4 text-white/90" />
                      </div>
                      <span className="flex-1 font-semibold text-white text-sm sm:text-base truncate">
                        {section.title}
                      </span>
                      <span className="shrink-0 text-white/50 transition-transform duration-200" style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
                        <ChevronDown className="w-4 h-4" />
                      </span>
                    </button>

                    <AnimatePresence initial={false}>
                      {isExpanded && (
                        <motion.div
                          id={`help-section-${section.id}`}
                          role="region"
                          aria-labelledby={`help-heading-${section.id}`}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2, ease: 'easeInOut' }}
                          className="overflow-hidden"
                        >
                          <div className="px-3 sm:px-4 pb-4 pt-0 border-t border-white/5 max-h-[min(70vh,420px)] overflow-y-auto">
                            {section.blocks ? (
                              <div className="space-y-3 pt-3 mb-4">
                                {section.blocks.map((block, i) => {
                                  if (block.type === 'paragraph') {
                                    return (
                                      <p key={i} className="text-xs sm:text-sm text-white/70 leading-relaxed">
                                        {block.text}
                                      </p>
                                    );
                                  }
                                  if (block.type === 'heading') {
                                    return (
                                      <p key={i} className="text-xs sm:text-sm font-semibold text-white mt-2 first:mt-0">
                                        {block.text}
                                      </p>
                                    );
                                  }
                                  if (block.type === 'list') {
                                    return (
                                      <ul key={i} className="list-disc list-inside text-xs sm:text-sm text-white/60 space-y-1 pl-1">
                                        {block.items.map((item, j) => (
                                          <li key={j} className="leading-relaxed">
                                            {item}
                                          </li>
                                        ))}
                                      </ul>
                                    );
                                  }
                                  return null;
                                })}
                              </div>
                            ) : (
                              <>
                                <p className="text-xs sm:text-sm text-white/70 leading-relaxed mb-3 pt-3">
                                  {section.description}
                                </p>
                                {section.details && section.details.length > 0 && (
                                  <ul className="list-disc list-inside text-xs sm:text-sm text-white/60 space-y-1.5 mb-4">
                                    {section.details.map((item, i) => (
                                      <li key={i} className="leading-relaxed">
                                        {item}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </>
                            )}
                            <button
                              type="button"
                              onClick={(e) => handleAction(section, e)}
                              className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-300 hover:text-indigo-200 transition-colors"
                            >
                              {getActionLabel(section)}
                              <ChevronRight className="w-4 h-4 shrink-0" />
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default HelpCenterModal;
